/**
 * Integrations Tab
 *
 * Google Calendar + Gmail connections, calendar preferences, and email capture address.
 */

import { useState, useEffect } from 'react';
import {
  Link,
  Unlink,
  Loader2,
  AlertCircle,
  Check,
  Calendar,
  RefreshCw,
  Mail,
  Copy,
  RotateCcw,
} from 'lucide-react';
import { btn } from '../buttonStyles';
import { replanDay } from '../../../api/calendar';
import { onGoogleConnectComplete } from '../../../api/googleConnectBridge';
import {
  getGoogleIntegrationStatus,
  disconnectGoogle,
  connectGoogle,
  getGoogleCalendars,
  getCalendarPreferences,
  saveCalendarPreferences,
  type GoogleIntegrationStatus,
  type GoogleCalendar,
} from '../../../api/integrations';
import { initCaptureEmail, rotateCaptureEmail } from '../../../api/userSettings';

export function IntegrationsTab() {
  const [googleStatus, setGoogleStatus] = useState<GoogleIntegrationStatus | null>(null);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);

  // Calendar preferences
  const [calendars, setCalendars] = useState<GoogleCalendar[]>([]);
  const [isLoadingCalendars, setIsLoadingCalendars] = useState(false);
  const [calendarError, setCalendarError] = useState<string | null>(null);
  const [readCalendarIds, setReadCalendarIds] = useState<Set<string>>(new Set());
  const [writeCalendarId, setWriteCalendarId] = useState<string | null>(null);
  const [plannerEnabled, setPlannerEnabled] = useState(false);
  const [plannerInstructions, setPlannerInstructions] = useState('');
  const [eventTitlePrefix, setEventTitlePrefix] = useState('');
  const [plannerMaxTasks, setPlannerMaxTasks] = useState(5);
  const [plannerStartHour, setPlannerStartHour] = useState(9);
  const [plannerEndHour, setPlannerEndHour] = useState(17);
  const [isSavingCalPrefs, setIsSavingCalPrefs] = useState(false);
  const [calPrefsSuccess, setCalPrefsSuccess] = useState<string | null>(null);

  // On-demand replan
  const [isReplanning, setIsReplanning] = useState(false);
  const [replanMessage, setReplanMessage] = useState<string | null>(null);

  const handleReplanDay = async () => {
    setIsReplanning(true);
    setReplanMessage(null);
    try {
      const result = await replanDay();
      if (result?.queued === false) {
        setReplanMessage("A replan is already running for today. Check back in a few minutes.");
      } else {
        setReplanMessage("Replan queued ✓ — Danny will roll over tasks and schedule your day. This runs in the background and may take a minute.");
      }
    } catch (err: any) {
      setReplanMessage(err.message || "Failed to replan. Make sure Google Calendar is connected.");
    } finally {
      setIsReplanning(false);
      setTimeout(() => setReplanMessage(null), 12000);
    }
  };

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Email capture address
  const [captureAddress, setCaptureAddress] = useState<string | null>(null);
  const [isCaptureLoading, setIsCaptureLoading] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  const [showRotateConfirm, setShowRotateConfirm] = useState(false);
  const [captureError, setCaptureError] = useState<string | null>(null);

  const loadCaptureEmail = async () => {
    setIsCaptureLoading(true);
    setCaptureError(null);
    try {
      // init is idempotent — returns existing address or creates one on first call
      const result = await initCaptureEmail();
      setCaptureAddress(result.address);
    } catch (err: any) {
      setCaptureError(err.message || 'Failed to load capture address.');
    } finally {
      setIsCaptureLoading(false);
    }
  };

  const handleCopyAddress = async () => {
    if (!captureAddress) return;
    await navigator.clipboard.writeText(captureAddress);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const handleRotateAddress = async () => {
    setIsRotating(true);
    setCaptureError(null);
    try {
      const result = await rotateCaptureEmail();
      setCaptureAddress(result.address);
      setShowRotateConfirm(false);
    } catch (err: any) {
      setCaptureError(err.message || 'Failed to rotate address.');
    } finally {
      setIsRotating(false);
    }
  };

  useEffect(() => {
    loadGoogleStatus();
    loadCaptureEmail();

    // Handle redirect back from Google OAuth
    const params = new URLSearchParams(window.location.search);
    const googleConnect = params.get('google_connect');
    if (googleConnect === 'success') {
      setSuccess('Google account connected successfully.');
      loadGoogleStatus();
    } else if (googleConnect === 'error') {
      setError('Failed to connect Google account. Please try again.');
    }
    if (googleConnect) {
      const clean = new URL(window.location.href);
      clean.searchParams.delete('google_connect');
      window.history.replaceState({}, '', clean.toString());
    }
  }, []);

  // Extension side panel: the OAuth popup can't update our URL, so it relays
  // the result back here (see googleConnectBridge). React the same way the
  // URL-param path above does.
  useEffect(() => {
    return onGoogleConnectComplete((result) => {
      if (result === 'error') {
        setError('Failed to connect Google account. Please try again.');
      } else {
        setSuccess('Google account connected successfully.');
        loadGoogleStatus();
      }
    });
    // loadGoogleStatus only touches stable setState — safe to bind once.
     
  }, []);

  const loadGoogleStatus = async () => {
    try {
      const status = await getGoogleIntegrationStatus();
      setGoogleStatus(status);
    } catch {
      setGoogleStatus({ connected: false });
    }
  };

  const handleConnectGoogle = () => {
    connectGoogle();
  };

  const handleDisconnectGoogle = async () => {
    setIsGoogleLoading(true);
    try {
      await disconnectGoogle();
      setGoogleStatus({ connected: false });
      setSuccess('Google account disconnected.');
    } catch (err: unknown) {
      setError((err as Error).message || 'Failed to disconnect Google account.');
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const loadCalendars = async () => {
    setIsLoadingCalendars(true);
    setCalendarError(null);
    try {
      const [{ calendars: cals }, prefs] = await Promise.all([
        getGoogleCalendars(),
        getCalendarPreferences().catch(() => null),
      ]);
      setCalendars(cals);
      setPlannerEnabled(prefs?.plannerEnabled ?? false);
      setPlannerInstructions(prefs?.plannerPrompt ?? '');
      setEventTitlePrefix(prefs?.eventTitlePrefix ?? '');
      setPlannerMaxTasks(prefs?.plannerMaxTasks ?? 5);
      setPlannerStartHour(prefs?.plannerStartHour ?? 9);
      setPlannerEndHour(prefs?.plannerEndHour ?? 17);

      if (prefs?.readCalendarIds && prefs.readCalendarIds.length > 0) {
        setReadCalendarIds(new Set(prefs.readCalendarIds));
      } else {
        setReadCalendarIds(new Set(cals.map((c) => c.id)));
      }
      if (prefs?.writeCalendarId) {
        setWriteCalendarId(prefs.writeCalendarId);
      } else {
        const primary = cals.find((c) => c.primary);
        setWriteCalendarId(primary?.id ?? cals[0]?.id ?? null);
      }
    } catch (err: any) {
      const detail = err?.message || String(err);
      setCalendarError(`Could not load calendars: ${detail}`);
    } finally {
      setIsLoadingCalendars(false);
    }
  };

  const handleSaveCalendarPrefs = async (newReadIds: Set<string>, newWriteId: string | null) => {
    setIsSavingCalPrefs(true);
    setCalPrefsSuccess(null);
    try {
      await saveCalendarPreferences({
        readCalendarIds: Array.from(newReadIds),
        writeCalendarId: newWriteId,
      });
      setCalPrefsSuccess('Preferences saved');
      setTimeout(() => setCalPrefsSuccess(null), 2000);
    } catch (err: unknown) {
      setError((err as Error).message || 'Failed to save calendar preferences.');
    } finally {
      setIsSavingCalPrefs(false);
    }
  };

  const handleToggleReadCalendar = (calId: string) => {
    const next = new Set(readCalendarIds);
    if (next.has(calId)) {
      next.delete(calId);
    } else {
      next.add(calId);
    }
    setReadCalendarIds(next);
    handleSaveCalendarPrefs(next, writeCalendarId);
  };

  const handleWriteCalendarChange = (calId: string) => {
    setWriteCalendarId(calId);
    handleSaveCalendarPrefs(readCalendarIds, calId);
  };

  useEffect(() => {
    if (googleStatus?.connected) {
      loadCalendars();
    }
  }, [googleStatus?.connected]);

  return (
    <>
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
          <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}
      {success && (
        <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-start gap-3">
          <Check className="w-5 h-5 text-green-600 dark:text-green-400 shrink-0 mt-0.5" />
          <p className="text-sm text-green-800 dark:text-green-200">{success}</p>
        </div>
      )}

      {/* Connected Apps */}
      <div className="bg-surface-raised-light dark:bg-surface-raised-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
            <Link className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark">Connected Apps</h2>
            <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
              Connect external accounts so Danny's AI assistant can check your calendar and draft emails.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between py-3 border-t border-border-subtle-light dark:border-border-subtle-dark">
          <div className="flex items-center gap-3">
            <svg className="w-6 h-6" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            <div>
              <p className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark">Google</p>
              <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                {googleStatus?.connected
                  ? 'Calendar (read) · Gmail (compose drafts)'
                  : 'Calendar access + Gmail draft creation'}
              </p>
            </div>
          </div>

          {googleStatus?.connected ? (
            <button
              onClick={handleDisconnectGoogle}
              disabled={isGoogleLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50"
            >
              {isGoogleLoading ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Unlink className="w-3.5 h-3.5" />
              )}
              Disconnect
            </button>
          ) : (
            <button
              onClick={handleConnectGoogle}
              className={`${btn.secondary.sm} flex items-center gap-1.5`}
            >
              <Link className="w-3.5 h-3.5" />
              Connect
            </button>
          )}
        </div>
      </div>

      {/* Calendar Preferences (only when Google is connected) */}
      {googleStatus?.connected && (
        <div className="bg-surface-raised-light dark:bg-surface-raised-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-teal-100 dark:bg-teal-900/30 rounded-lg">
              <Calendar className="w-5 h-5 text-teal-600 dark:text-teal-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark">Calendar Preferences</h2>
              <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                Choose which calendars Danny can see and where to add events.
              </p>
            </div>
          </div>

          {/* Planner toggle */}
          <div className="flex items-center justify-between py-1">
            <div>
              <p className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark">Enable daily planner</p>
              <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark mt-0.5">
                Danny schedules your tasks on Google Calendar each morning
              </p>
            </div>
            <button
              role="switch"
              aria-checked={plannerEnabled}
              onClick={async () => {
                const next = !plannerEnabled;
                setPlannerEnabled(next);
                try {
                  await saveCalendarPreferences({ plannerEnabled: next });
                } catch {
                  setPlannerEnabled(!next); // revert on error
                }
              }}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                plannerEnabled ? 'bg-primary-600' : 'bg-border-hover-light dark:bg-border-hover-dark'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                plannerEnabled ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>

          {/* Planner controls + Replan — only when planner is on */}
          {plannerEnabled && (
            <div className="space-y-3 border-t border-border-subtle-light dark:border-border-subtle-dark pt-3">

              {/* Row 1: Event prefix + Max tasks */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-text-secondary-light dark:text-text-secondary-dark block mb-1">
                    Event prefix
                  </label>
                  <input
                    type="text"
                    value={eventTitlePrefix}
                    onChange={(e) => setEventTitlePrefix(e.target.value)}
                    onBlur={() => saveCalendarPreferences({ eventTitlePrefix: eventTitlePrefix || null })}
                    placeholder="e.g. ☑"
                    maxLength={10}
                    className="w-full px-3 py-1.5 text-sm bg-surface-base-light dark:bg-surface-hover-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg text-text-primary-light dark:text-text-primary-dark placeholder:text-text-tertiary-light dark:placeholder:text-text-tertiary-dark focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-text-secondary-light dark:text-text-secondary-dark block mb-1">
                    Max tasks/day
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={15}
                    value={plannerMaxTasks}
                    onChange={(e) => setPlannerMaxTasks(Number(e.target.value))}
                    onBlur={() => saveCalendarPreferences({ plannerMaxTasks })}
                    className="w-full px-3 py-1.5 text-sm bg-surface-base-light dark:bg-surface-hover-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg text-text-primary-light dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>

              {/* Row 2: Scheduling window */}
              <div>
                <label className="text-xs font-medium text-text-secondary-light dark:text-text-secondary-dark block mb-1">
                  Scheduling window (local time)
                </label>
                <div className="flex items-center gap-2">
                  <select
                    value={plannerStartHour}
                    onChange={(e) => { setPlannerStartHour(Number(e.target.value)); saveCalendarPreferences({ plannerStartHour: Number(e.target.value) }); }}
                    className="flex-1 px-2 py-1.5 text-sm bg-surface-base-light dark:bg-surface-hover-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg text-text-primary-light dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {Array.from({ length: 24 }, (_, i) => (
                      <option key={i} value={i}>{i === 0 ? '12 AM' : i < 12 ? `${i} AM` : i === 12 ? '12 PM' : `${i - 12} PM`}</option>
                    ))}
                  </select>
                  <span className="text-xs text-text-tertiary-light dark:text-text-tertiary-dark shrink-0">to</span>
                  <select
                    value={plannerEndHour}
                    onChange={(e) => { setPlannerEndHour(Number(e.target.value)); saveCalendarPreferences({ plannerEndHour: Number(e.target.value) }); }}
                    className="flex-1 px-2 py-1.5 text-sm bg-surface-base-light dark:bg-surface-hover-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg text-text-primary-light dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {Array.from({ length: 24 }, (_, i) => (
                      <option key={i} value={i}>{i === 0 ? '12 AM' : i < 12 ? `${i} AM` : i === 12 ? '12 PM' : `${i - 12} PM`}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Row 3: Instructions textarea + Replan */}
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium text-text-secondary-light dark:text-text-secondary-dark uppercase tracking-wider">
                  Custom instructions
                </p>
                <button
                  onClick={handleReplanDay}
                  disabled={isReplanning}
                  className={`${btn.primary.sm} whitespace-nowrap`}
                >
                  {isReplanning
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : <RefreshCw className="w-3.5 h-3.5" />}
                  {isReplanning ? 'Replanning…' : 'Replan now'}
                </button>
              </div>
              <textarea
                value={plannerInstructions}
                onChange={(e) => setPlannerInstructions(e.target.value)}
                onBlur={async () => {
                  await saveCalendarPreferences({ plannerPrompt: plannerInstructions || null });
                }}
                placeholder="e.g. Add 10 min buffer between tasks. Focus deep work before noon. Skip tasks tagged #someday. Max 2 hours of meetings."
                rows={3}
                className="w-full px-3 py-2 text-sm bg-surface-base-light dark:bg-surface-hover-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg text-text-primary-light dark:text-text-primary-dark placeholder:text-text-tertiary-light dark:placeholder:text-text-tertiary-dark resize-none focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              {replanMessage && (
                <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">{replanMessage}</p>
              )}
            </div>
          )}

          {isLoadingCalendars ? (
            <div className="space-y-3 pt-2">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-4 h-4 bg-surface-hover-light dark:bg-surface-hover-dark rounded animate-pulse" />
                  <div className="w-3 h-3 rounded-full bg-surface-hover-light dark:bg-surface-hover-dark animate-pulse" />
                  <div className="h-4 flex-1 bg-surface-hover-light dark:bg-surface-hover-dark rounded animate-pulse" />
                </div>
              ))}
            </div>
          ) : calendarError ? (
            <div className="flex items-start gap-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <AlertCircle className="w-4 h-4 text-red-500 dark:text-red-400 shrink-0 mt-0.5" />
              <p className="text-sm text-red-700 dark:text-red-300">{calendarError}</p>
            </div>
          ) : calendars.length > 0 ? (
            <>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark">
                  Calendars Danny can read
                </label>
                <div className="space-y-1">
                  {calendars.map((cal) => (
                    <label
                      key={cal.id}
                      className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark/50 transition-colors cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={readCalendarIds.has(cal.id)}
                        onChange={() => handleToggleReadCalendar(cal.id)}
                        className="w-4 h-4 rounded border-border-subtle-light dark:border-border-subtle-dark text-teal-600 focus:ring-teal-500"
                      />
                      {cal.color && (
                        <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: cal.color }} />
                      )}
                      <span className="text-sm text-text-primary-light dark:text-text-primary-dark">
                        {cal.name}
                        {cal.primary && (
                          <span className="ml-1.5 text-xs text-text-secondary-light dark:text-text-secondary-dark">(Primary)</span>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark">
                  Calendar for new events
                </label>
                <select
                  value={writeCalendarId ?? ''}
                  onChange={(e) => handleWriteCalendarChange(e.target.value)}
                  className="w-full px-3 py-2 bg-surface-raised-light dark:bg-surface-raised-dark border border-border-hover-light dark:border-border-subtle-dark rounded-lg text-sm text-text-primary-light dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  {calendars
                    .filter((c) => c.accessRole === 'owner' || c.accessRole === 'writer')
                    .map((cal) => (
                      <option key={cal.id} value={cal.id}>
                        {cal.name}{cal.primary ? ' (Primary)' : ''}
                      </option>
                    ))}
                </select>
                <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                  Danny will create events on this calendar when scheduling tasks.
                </p>
              </div>

              {(isSavingCalPrefs || calPrefsSuccess) && (
                <div className="flex items-center gap-2 text-sm">
                  {isSavingCalPrefs ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin text-text-secondary-light dark:text-text-secondary-dark" />
                      <span className="text-text-secondary-light dark:text-text-secondary-dark">Saving...</span>
                    </>
                  ) : (
                    <>
                      <Check className="w-3.5 h-3.5 text-green-600 dark:text-green-400" />
                      <span className="text-green-700 dark:text-green-400">{calPrefsSuccess}</span>
                    </>
                  )}
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
              No calendars found. Make sure your Google account has calendar access.
            </p>
          )}
        </div>
      )}

      {/* Email Capture */}
      <div className="bg-surface-raised-light dark:bg-surface-raised-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
            <Mail className="w-5 h-5 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark">Email Capture</h2>
            <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
              Forward any email to this address to create a task automatically.
            </p>
          </div>
        </div>

        {captureError && (
          <div className="flex items-start gap-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <AlertCircle className="w-4 h-4 text-red-500 dark:text-red-400 shrink-0 mt-0.5" />
            <p className="text-sm text-red-700 dark:text-red-300">{captureError}</p>
          </div>
        )}

        {isCaptureLoading ? (
          <div className="flex items-center gap-2 text-sm text-text-secondary-light dark:text-text-secondary-dark">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>Loading your capture address...</span>
          </div>
        ) : captureAddress ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <code className="flex-1 px-3 py-2 bg-surface-hover-light dark:bg-surface-hover-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg text-sm font-mono text-text-primary-light dark:text-text-primary-dark truncate">
                {captureAddress}
              </code>
              <button
                onClick={handleCopyAddress}
                className={btn.secondary.sm}
                title="Copy to clipboard"
              >
                {copySuccess ? (
                  <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
                <span className="ml-1.5">{copySuccess ? 'Copied!' : 'Copy'}</span>
              </button>
            </div>

            <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
              The subject becomes the task title and the body becomes the notes.
            </p>

            {showRotateConfirm ? (
              <div className="flex items-center gap-2">
                <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark flex-1">
                  This will invalidate your current address. Are you sure?
                </p>
                <button
                  onClick={() => setShowRotateConfirm(false)}
                  className={btn.secondary.sm}
                >
                  Cancel
                </button>
                <button
                  onClick={handleRotateAddress}
                  disabled={isRotating}
                  className={btn.danger.sm}
                >
                  {isRotating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                  <span className="ml-1.5">Regenerate</span>
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowRotateConfirm(true)}
                className="text-xs text-text-secondary-light dark:text-text-secondary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark underline"
              >
                Regenerate address
              </button>
            )}
          </div>
        ) : null}
      </div>
    </>
  );
}
